import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Switch } from './ui/switch';
import { 
  Plus, Play, Pause, Trash2, Edit, Copy, ChevronRight, ChevronDown, ChevronUp,
  Zap, FileText, Building2, TrendingUp, CheckSquare, UserPlus, Clock,
  Webhook, Mail, MessageCircle, StickyNote, Bot, Table, Receipt, Calendar,
  ArrowRight, Timer, GitBranch, Settings, Eye, MoreVertical, Loader2,
  AlertCircle, CheckCircle, XCircle, History, Search, Filter, GripVertical,
  Sparkles, Send, FolderOpen, ExternalLink, RefreshCw, Bell, Trophy, Cloud,
  Brain, LayoutTemplate, MousePointer, GripHorizontal
} from 'lucide-react';
import * as api from '../services/api';

const ICON_MAP = {
  FileText, Building2, TrendingUp, CheckSquare, UserPlus, Clock, Play, Webhook,
  Mail, MessageCircle, StickyNote, Bot, Table, Receipt, ArrowRight, Timer, GitBranch,
  Zap, Calendar, Sparkles, Send, Search, Bell, Trophy, Cloud, Brain
};

// Key-Value editor component for dynamic key-value pairs
const KeyValueEditor = ({ value = {}, onChange }) => {
  const [pairs, setPairs] = useState(
    Object.entries(value).length > 0 
      ? Object.entries(value).map(([k, v]) => ({ key: k, value: v }))
      : [{ key: '', value: '' }]
  );

  const updatePairs = (newPairs) => {
    setPairs(newPairs);
    const obj = {};
    newPairs.forEach(p => {
      if (p.key) obj[p.key] = p.value;
    });
    onChange(obj);
  };

  const addPair = () => {
    updatePairs([...pairs, { key: '', value: '' }]);
  };

  const removePair = (index) => {
    updatePairs(pairs.filter((_, i) => i !== index));
  };

  const updatePair = (index, field, val) => {
    const newPairs = [...pairs];
    newPairs[index][field] = val;
    updatePairs(newPairs);
  };

  return (
    <div className="space-y-2">
      {pairs.map((pair, index) => (
        <div key={index} className="flex gap-2 items-center">
          <Input
            placeholder="Clave (ej: columna_A)"
            value={pair.key}
            onChange={(e) => updatePair(index, 'key', e.target.value)}
            className="flex-1"
          />
          <Input
            placeholder="Valor (ej: {{empresa.name}})"
            value={pair.value}
            onChange={(e) => updatePair(index, 'value', e.target.value)}
            className="flex-1"
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => removePair(index)}
            className="text-red-500 p-1"
          >
            <Trash2 size={14} />
          </Button>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addPair} className="w-full">
        <Plus size={14} className="mr-1" /> Agregar Campo
      </Button>
    </div>
  );
};

const AutomationsPage = () => {
  const [automations, setAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBuilder, setShowBuilder] = useState(false);
  const [editingAutomation, setEditingAutomation] = useState(null);
  const [config, setConfig] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [recentLogs, setRecentLogs] = useState([]);
  const [showLogs, setShowLogs] = useState(false);
  const [selectedAutomationLogs, setSelectedAutomationLogs] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [templates, setTemplates] = useState([]);

  // Data sources for dropdowns
  const [forms, setForms] = useState([]);
  const [users, setUsers] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [agents, setAgents] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [dynamicResources, setDynamicResources] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [autoRes, configRes, formsRes, usersRes, pipelinesRes, agentsRes, templatesRes, resourcesRes, autoTemplatesRes] = await Promise.all([
        api.default.get('/automations'),
        api.default.get('/automations/config'),
        api.default.get('/forms').catch(() => ({ data: [] })),
        api.getUsers().catch(() => ({ data: [] })),
        api.getPipelines().catch(() => ({ data: [] })),
        api.default.get('/agents').catch(() => ({ data: [] })),
        api.getEmailTemplates().catch(() => ({ data: [] })),
        api.default.get('/automations/resources').catch(() => ({ data: {} })),
        api.default.get('/automations/templates').catch(() => ({ data: { templates: [], categories: [] } }))
      ]);
      
      setAutomations(autoRes.data);
      setConfig(configRes.data);
      setForms(formsRes.data);
      setUsers(usersRes.data);
      setPipelines(pipelinesRes.data);
      setAgents(agentsRes.data);
      setEmailTemplates(templatesRes.data);
      setDynamicResources(resourcesRes.data || {});
      setTemplates(autoTemplatesRes.data?.templates || []);
    } catch (error) {
      console.error('Error loading automations:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggle = async (id) => {
    try {
      const res = await api.default.post(`/automations/${id}/toggle`);
      setAutomations(prev => prev.map(a => 
        a.id === id ? { ...a, is_active: res.data.is_active } : a
      ));
    } catch (error) {
      console.error('Error toggling automation:', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta automatización?')) return;
    try {
      await api.default.delete(`/automations/${id}`);
      setAutomations(prev => prev.filter(a => a.id !== id));
    } catch (error) {
      console.error('Error deleting automation:', error);
    }
  };

  const handleRun = async (id) => {
    try {
      const res = await api.default.post(`/automations/${id}/run`);
      alert(`Automatización iniciada. Log ID: ${res.data.log_id}`);
      loadData();
    } catch (error) {
      console.error('Error running automation:', error);
      alert('Error al ejecutar automatización');
    }
  };

  const handleViewLogs = async (automationId) => {
    try {
      const res = await api.default.get(`/automations/${automationId}/logs`);
      setSelectedAutomationLogs({ id: automationId, logs: res.data });
      setShowLogs(true);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const handleDuplicate = async (automation) => {
    const newAutomation = {
      name: `${automation.name} (Copia)`,
      description: automation.description,
      trigger: automation.trigger,
      conditions: automation.conditions,
      actions: automation.actions,
      is_active: false
    };
    
    try {
      const res = await api.default.post('/automations', newAutomation);
      setAutomations(prev => [res.data, ...prev]);
    } catch (error) {
      console.error('Error duplicating automation:', error);
    }
  };

  const handleUseTemplate = async (templateId) => {
    try {
      const res = await api.default.post(`/automations/from-template/${templateId}`);
      setAutomations(prev => [res.data, ...prev]);
      setShowTemplates(false);
      // Open the builder to edit the new automation
      setEditingAutomation(res.data);
      setShowBuilder(true);
    } catch (error) {
      console.error('Error creating from template:', error);
      alert('Error al crear automatización desde plantilla');
    }
  };

  const filteredAutomations = automations.filter(a => {
    const matchesSearch = a.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (a.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterActive === null || a.is_active === filterActive;
    return matchesSearch && matchesFilter;
  });

  const getTriggerIcon = (type) => {
    const trigger = config?.trigger_types?.find(t => t.id === type);
    const IconComponent = ICON_MAP[trigger?.icon] || Zap;
    return <IconComponent size={18} />;
  };

  const getTriggerName = (type) => {
    const trigger = config?.trigger_types?.find(t => t.id === type);
    return trigger?.name || type;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-[#D4A843]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="automations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Automatizaciones</h1>
          <p className="text-gray-500 mt-1">Crea flujos de trabajo automáticos para tu CRM</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline"
            onClick={() => setShowTemplates(true)}
            className="border-[#D4A843] text-[#D4A843] hover:bg-[#D4A843]/10"
          >
            <LayoutTemplate className="mr-2 h-4 w-4" />
            Usar Plantilla
          </Button>
          <Button 
            onClick={() => { setEditingAutomation(null); setShowBuilder(true); }}
            className="bg-[#D4A843] hover:bg-[#c49a3a]"
            data-testid="new-automation-btn"
          >
            <Plus className="mr-2 h-4 w-4" />
            Nueva Automatización
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Buscar automatizaciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        <div className="flex gap-2">
          <Button
            variant={filterActive === null ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterActive(null)}
          >
            Todas
          </Button>
          <Button
            variant={filterActive === true ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterActive(true)}
          >
            <CheckCircle className="mr-1 h-4 w-4" />
            Activas
          </Button>
          <Button
            variant={filterActive === false ? "default" : "outline"}
            size="sm"
            onClick={() => setFilterActive(false)}
          >
            <Pause className="mr-1 h-4 w-4" />
            Pausadas
          </Button>
        </div>
      </div>

      {/* Automations List */}
      {filteredAutomations.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Zap className="h-12 w-12 text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm || filterActive !== null ? 'No se encontraron automatizaciones' : 'No hay automatizaciones'}
            </h3>
            <p className="text-gray-500 text-center mb-4">
              {searchTerm || filterActive !== null 
                ? 'Intenta con otros filtros de búsqueda'
                : 'Crea tu primera automatización para ahorrar tiempo en tareas repetitivas'}
            </p>
            {!searchTerm && filterActive === null && (
              <Button onClick={() => setShowBuilder(true)} className="bg-[#D4A843] hover:bg-[#c49a3a]">
                <Plus className="mr-2 h-4 w-4" />
                Crear Automatización
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {filteredAutomations.map(automation => (
            <Card key={automation.id} className={`transition-all ${!automation.is_active ? 'opacity-60' : ''}`} data-testid={`automation-card-${automation.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`p-3 rounded-lg ${automation.is_active ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'}`}>
                      {getTriggerIcon(automation.trigger?.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{automation.name}</h3>
                        <Badge variant={automation.is_active ? "default" : "secondary"} className={automation.is_active ? 'bg-green-100 text-green-700' : ''}>
                          {automation.is_active ? 'Activa' : 'Pausada'}
                        </Badge>
                      </div>
                      {automation.description && (
                        <p className="text-sm text-gray-500 mt-1">{automation.description}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Zap size={14} />
                          {getTriggerName(automation.trigger?.type)}
                        </span>
                        <span className="flex items-center gap-1">
                          <ChevronRight size={14} />
                          {automation.actions?.length || 0} acciones
                        </span>
                        <span className="flex items-center gap-1">
                          <History size={14} />
                          {automation.total_runs || 0} ejecuciones
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={automation.is_active}
                      onCheckedChange={() => handleToggle(automation.id)}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRun(automation.id)}
                      disabled={!automation.is_active}
                      title="Ejecutar ahora"
                    >
                      <Play size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleViewLogs(automation.id)}
                      title="Ver historial"
                    >
                      <History size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setEditingAutomation(automation); setShowBuilder(true); }}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDuplicate(automation)}
                      title="Duplicar"
                    >
                      <Copy size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(automation.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                      title="Eliminar"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Builder Modal */}
      {showBuilder && (
        <AutomationBuilder
          automation={editingAutomation}
          config={config}
          forms={forms}
          users={users}
          pipelines={pipelines}
          agents={agents}
          emailTemplates={emailTemplates}
          dynamicResources={dynamicResources}
          onClose={() => { setShowBuilder(false); setEditingAutomation(null); }}
          onSave={() => { setShowBuilder(false); setEditingAutomation(null); loadData(); }}
        />
      )}

      {/* Logs Modal */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Historial de Ejecuciones</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {selectedAutomationLogs?.logs?.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No hay ejecuciones registradas</p>
            ) : (
              selectedAutomationLogs?.logs?.map(log => (
                <Card key={log.id} className="p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      {log.status === 'completed' && <CheckCircle className="text-green-500" size={20} />}
                      {log.status === 'failed' && <XCircle className="text-red-500" size={20} />}
                      {log.status === 'pending' && <Loader2 className="text-yellow-500 animate-spin" size={20} />}
                      {log.status === 'completed_with_errors' && <AlertCircle className="text-orange-500" size={20} />}
                      <div>
                        <p className="font-medium">{log.trigger_type}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(log.started_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    <Badge variant={
                      log.status === 'completed' ? 'default' :
                      log.status === 'failed' ? 'destructive' :
                      'secondary'
                    }>
                      {log.status}
                    </Badge>
                  </div>
                  {log.errors?.length > 0 && (
                    <div className="mt-2 p-2 bg-red-50 rounded text-sm text-red-600">
                      {log.errors.map((e, i) => (
                        <p key={i}>{e.action_type}: {e.error}</p>
                      ))}
                    </div>
                  )}
                </Card>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Templates Modal */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LayoutTemplate className="text-[#D4A843]" size={24} />
              Plantillas de Automatización
            </DialogTitle>
            <DialogDescription>
              Selecciona una plantilla predefinida para comenzar rápidamente
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
            {templates.map(template => {
              const IconComponent = ICON_MAP[template.icon] || Zap;
              return (
                <Card 
                  key={template.id} 
                  className="cursor-pointer hover:border-[#D4A843] hover:shadow-md transition-all"
                  onClick={() => handleUseTemplate(template.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-lg bg-[#D4A843]/10 text-[#D4A843]">
                        <IconComponent size={24} />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900">{template.name}</h3>
                        <p className="text-sm text-gray-500 mt-1">{template.description}</p>
                        <div className="flex items-center gap-2 mt-3">
                          <Badge variant="outline" className="text-xs">
                            {template.trigger?.type?.replace(/_/g, ' ')}
                          </Badge>
                          <span className="text-xs text-gray-400">
                            {template.actions?.length} acciones
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          
          {templates.length === 0 && (
            <div className="text-center py-8 text-gray-500">
              <LayoutTemplate size={48} className="mx-auto mb-4 opacity-50" />
              <p>No hay plantillas disponibles</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ==================== AUTOMATION BUILDER COMPONENT ====================

const AutomationBuilder = ({ automation, config, forms, users, pipelines, agents, emailTemplates, dynamicResources = {}, onClose, onSave }) => {
  const [name, setName] = useState(automation?.name || '');
  const [description, setDescription] = useState(automation?.description || '');
  const [trigger, setTrigger] = useState(automation?.trigger || { type: '', config: {} });
  const [actions, setActions] = useState(automation?.actions || []);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('trigger');
  const [showActionPicker, setShowActionPicker] = useState(false);
  const [expandedAction, setExpandedAction] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  
  // Track focused field for variable insertion
  const [focusedField, setFocusedField] = useState(null); // { actionIndex, fieldKey, ref }
  const fieldRefs = useRef({}); // Store refs for all input/textarea fields
  
  // Register a field ref for variable insertion
  const registerFieldRef = (actionIndex, fieldKey, ref) => {
    const key = `${actionIndex}-${fieldKey}`;
    if (ref) {
      fieldRefs.current[key] = ref;
    }
  };
  
  // Insert variable at cursor position
  const insertVariableAtCursor = (variable) => {
    if (!focusedField) {
      // No focused field, show a toast or alert
      alert('Primero haz clic en el campo donde quieres insertar la variable');
      return;
    }
    
    const { actionIndex, fieldKey } = focusedField;
    const key = `${actionIndex}-${fieldKey}`;
    const inputRef = fieldRefs.current[key];
    
    if (inputRef) {
      const input = inputRef;
      const start = input.selectionStart || 0;
      const end = input.selectionEnd || 0;
      const currentValue = actions[actionIndex]?.config[fieldKey] || '';
      const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
      
      // Update the action config
      updateAction(actionIndex, { ...actions[actionIndex].config, [fieldKey]: newValue });
      
      // Restore focus and cursor position after state update
      setTimeout(() => {
        input.focus();
        const newCursorPos = start + variable.length;
        input.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };
  
  // Handle drag start for variables
  const handleVariableDragStart = (e, variable) => {
    e.dataTransfer.setData('text/plain', variable);
    e.dataTransfer.effectAllowed = 'copy';
  };
  
  // Handle drop on input/textarea fields
  const handleFieldDrop = (e, actionIndex, fieldKey) => {
    e.preventDefault();
    const variable = e.dataTransfer.getData('text/plain');
    if (variable && variable.startsWith('{{')) {
      const currentValue = actions[actionIndex]?.config[fieldKey] || '';
      const newValue = currentValue + variable;
      updateAction(actionIndex, { ...actions[actionIndex].config, [fieldKey]: newValue });
    }
  };
  
  const handleFieldDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  // Function to load email template data and auto-fill fields
  const loadEmailTemplate = async (templateId, actionIndex) => {
    if (!templateId) return;
    
    setLoadingTemplate(true);
    try {
      const response = await api.default.get(`/agents/email-templates/${templateId}`);
      // API returns {template: {...}, available_variables: [...]}
      const template = response.data.template || response.data;
      
      if (template) {
        // Auto-fill the action config with template data
        const currentAction = actions[actionIndex];
        const updatedConfig = {
          ...currentAction.config,
          template_id: templateId,
          subject: template.subject || '',
          body: template.body || '',
          _template_attachments: template.attachments || [],
          _template_name: template.name || ''
        };
        updateAction(actionIndex, updatedConfig);
      }
    } catch (error) {
      console.error('Error loading template:', error);
    } finally {
      setLoadingTemplate(false);
    }
  };

  const handleSave = async () => {
    if (!name || !trigger.type || actions.length === 0) {
      alert('Por favor completa el nombre, trigger y al menos una acción');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name,
        description,
        trigger,
        conditions: [],
        actions: actions.map((a, i) => ({ ...a, order: i })),
        is_active: automation?.is_active ?? true
      };

      if (automation?.id) {
        await api.default.put(`/automations/${automation.id}`, payload);
      } else {
        await api.default.post('/automations', payload);
      }
      onSave();
    } catch (error) {
      console.error('Error saving automation:', error);
      alert('Error al guardar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSaving(false);
    }
  };

  const addAction = (actionType) => {
    const actionConfig = config?.action_types?.find(a => a.id === actionType);
    const newAction = { type: actionType, config: {}, order: actions.length };
    setActions([...actions, newAction]);
    setExpandedAction(actions.length); // Expand the newly added action
    setShowActionPicker(false);
  };

  const updateAction = (index, newConfig) => {
    setActions(prev => prev.map((a, i) => i === index ? { ...a, config: newConfig } : a));
  };

  const removeAction = (index) => {
    setActions(prev => prev.filter((_, i) => i !== index));
    if (expandedAction === index) setExpandedAction(null);
  };

  const moveAction = (index, direction) => {
    const newActions = [...actions];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= actions.length) return;
    [newActions[index], newActions[newIndex]] = [newActions[newIndex], newActions[index]];
    setActions(newActions);
  };

  const getDataSource = (source) => {
    // First check dynamic resources
    if (dynamicResources[source]) {
      return dynamicResources[source];
    }
    
    switch (source) {
      case 'forms': return forms.map(f => ({ value: f.id, label: f.name }));
      case 'users': return users.map(u => ({ value: u.id, label: u.name }));
      case 'pipelines': return pipelines.map(p => ({ value: p.id, label: p.name }));
      case 'agents': return agents.map(a => ({ value: a.id, label: a.name }));
      case 'email_templates': 
        return dynamicResources.email_templates?.length > 0 
          ? dynamicResources.email_templates 
          : emailTemplates.map(t => ({ value: t.id, label: t.name || t.subject }));
      case 'stages': return ['Lead', 'Contactado', 'Propuesta', 'Negociación', 'Ganado', 'Perdido'].map(s => ({ value: s, label: s }));
      case 'google_sheets': return dynamicResources.google_sheets || [];
      case 'google_doc_templates': return dynamicResources.google_doc_templates || [];
      case 'google_folders': return dynamicResources.google_folders || [];
      case 'gamma_themes': return dynamicResources.gamma_themes || [];
      case 'pipeline_stages':
        // Get stages from all pipelines
        return pipelines.flatMap(p => 
          (p.stages || []).map(s => ({ value: s.id, label: `${p.name} - ${s.name}` }))
        );
      default: return [];
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {automation ? 'Editar Automatización' : 'Nueva Automatización'}
          </DialogTitle>
          <DialogDescription>
            Configura el disparador y las acciones de tu automatización
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          <div className="space-y-6 pb-4">
            {/* Basic Info */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nombre *</label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ej: Seguimiento automático de leads"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Descripción</label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Descripción opcional"
                />
              </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="trigger">
                  <Zap className="mr-2 h-4 w-4" />
                  Disparador
                </TabsTrigger>
                <TabsTrigger value="actions">
                  <Play className="mr-2 h-4 w-4" />
                  Acciones ({actions.length})
                </TabsTrigger>
              </TabsList>

              {/* Trigger Tab */}
              <TabsContent value="trigger" className="space-y-4 mt-4">
                <p className="text-sm text-gray-500">¿Cuándo debe activarse esta automatización?</p>
                
                <div className="grid grid-cols-2 gap-3">
                  {config?.trigger_types?.map(t => {
                    const IconComponent = ICON_MAP[t.icon] || Zap;
                    const isSelected = trigger.type === t.id;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setTrigger({ type: t.id, config: {} })}
                        className={`p-4 border rounded-lg cursor-pointer transition-all ${
                          isSelected 
                            ? 'border-[#D4A843] bg-[#D4A843]/10' 
                            : 'border-gray-200 hover:border-[#D4A843]/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded ${isSelected ? 'bg-[#D4A843] text-white' : 'bg-gray-100'}`}>
                            <IconComponent size={20} />
                          </div>
                          <div>
                            <p className="font-medium">{t.name}</p>
                            <p className="text-xs text-gray-500">{t.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Trigger Config */}
                {trigger.type && (
                  <Card className="p-4 bg-gray-50">
                    <h4 className="font-medium mb-3">Configuración del disparador</h4>
                    {config?.trigger_types?.find(t => t.id === trigger.type)?.config_fields?.map(field => (
                      <div key={field.key} className="mb-3">
                        <label className="block text-sm font-medium mb-1">
                          {field.label} {!field.optional && '*'}
                        </label>
                        {field.type === 'select' ? (
                          <Select
                            value={trigger.config[field.key] || ''}
                            onValueChange={(v) => setTrigger({
                              ...trigger,
                              config: { ...trigger.config, [field.key]: v }
                            })}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder={`Selecciona ${field.label.toLowerCase()}`} />
                            </SelectTrigger>
                            <SelectContent>
                              {(field.options 
                                ? field.options.map(o => ({ value: o, label: o }))
                                : getDataSource(field.source)
                              ).map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>
                                  {opt.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : field.type === 'time' ? (
                          <Input
                            type="time"
                            value={trigger.config[field.key] || ''}
                            onChange={(e) => setTrigger({
                              ...trigger,
                              config: { ...trigger.config, [field.key]: e.target.value }
                            })}
                          />
                        ) : (
                          <Input
                            value={trigger.config[field.key] || ''}
                            onChange={(e) => setTrigger({
                              ...trigger,
                              config: { ...trigger.config, [field.key]: e.target.value }
                            })}
                          />
                        )}
                      </div>
                    ))}
                    {config?.trigger_types?.find(t => t.id === trigger.type)?.config_fields?.length === 0 && (
                      <p className="text-sm text-gray-500">Este disparador no requiere configuración adicional.</p>
                    )}
                  </Card>
                )}
              </TabsContent>

              {/* Actions Tab */}
              <TabsContent value="actions" className="space-y-4 mt-4">
                <p className="text-sm text-gray-500">¿Qué acciones debe realizar?</p>
                
                {/* Actions List */}
                <div className="space-y-3">
                  {actions.map((action, index) => {
                    const actionType = config?.action_types?.find(a => a.id === action.type);
                    const IconComponent = ICON_MAP[actionType?.icon] || Play;
                    
                    return (
                      <Card key={index} className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="flex flex-col gap-1">
                              <button 
                                onClick={() => moveAction(index, -1)}
                                className="p-1 hover:bg-gray-100 rounded"
                                disabled={index === 0}
                              >
                                <ChevronDown className="rotate-180" size={14} />
                              </button>
                              <button 
                                onClick={() => moveAction(index, 1)}
                                className="p-1 hover:bg-gray-100 rounded"
                                disabled={index === actions.length - 1}
                              >
                                <ChevronDown size={14} />
                              </button>
                            </div>
                            <div className="p-2 rounded bg-blue-100 text-blue-600">
                              <IconComponent size={18} />
                            </div>
                            <div>
                              <p className="font-medium">{actionType?.name || action.type}</p>
                              <p className="text-xs text-gray-500">{actionType?.description}</p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => removeAction(index)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                        
                        {/* Action Config Fields */}
                        <div className="pl-12 space-y-3">
                          {/* Show template auto-fill indicator */}
                          {action.config._template_name && action.type === 'send_email' && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-2">
                              <LayoutTemplate size={16} className="text-amber-600" />
                              <div className="flex-1">
                                <p className="text-sm text-amber-800 font-medium">
                                  Plantilla cargada: {action.config._template_name}
                                </p>
                                <p className="text-xs text-amber-600">
                                  Los campos de asunto y contenido se han auto-llenado. Puedes editarlos si lo deseas.
                                </p>
                              </div>
                              {loadingTemplate && <Loader2 className="animate-spin text-amber-600" size={16} />}
                            </div>
                          )}
                          
                          {actionType?.config_fields?.map(field => (
                            <div key={field.key}>
                              <label className="block text-sm font-medium mb-1">
                                {field.label} {!field.optional && '*'}
                                {field.description && (
                                  <span className="text-xs text-gray-400 ml-2">{field.description}</span>
                                )}
                                {/* Show "from template" badge if field was auto-filled */}
                                {action.config._template_name && (field.key === 'subject' || field.key === 'body') && action.config[field.key] && (
                                  <Badge variant="outline" className="ml-2 text-xs bg-amber-50 text-amber-700 border-amber-300">
                                    Auto-llenado
                                  </Badge>
                                )}
                              </label>
                              {field.type === 'select' ? (
                                <Select
                                  value={action.config[field.key] || ''}
                                  onValueChange={(v) => {
                                    updateAction(index, { ...action.config, [field.key]: v === '__none__' ? '' : v });
                                    // If selecting an email template, load its data
                                    if (field.key === 'template_id' && field.source === 'email_templates' && v && v !== '__none__') {
                                      loadEmailTemplate(v, index);
                                    }
                                  }}
                                >
                                  <SelectTrigger className={loadingTemplate && field.key === 'template_id' ? 'border-amber-400' : ''}>
                                    <SelectValue placeholder={`Selecciona ${field.label.toLowerCase()}`} />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {field.key === 'template_id' && (
                                      <SelectItem value="__none__">-- Sin plantilla (escribir manualmente) --</SelectItem>
                                    )}
                                    {(field.options 
                                      ? field.options.map(o => ({ value: o, label: o }))
                                      : getDataSource(field.source)
                                    ).map(opt => (
                                      <SelectItem key={opt.value} value={opt.value || '__empty__'}>
                                        {opt.label}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              ) : field.type === 'textarea' ? (
                                <Textarea
                                  ref={(ref) => registerFieldRef(index, field.key, ref)}
                                  value={action.config[field.key] || ''}
                                  onChange={(e) => updateAction(index, { ...action.config, [field.key]: e.target.value })}
                                  onFocus={() => setFocusedField({ actionIndex: index, fieldKey: field.key })}
                                  onDrop={(e) => handleFieldDrop(e, index, field.key)}
                                  onDragOver={handleFieldDragOver}
                                  placeholder={field.placeholder || ''}
                                  rows={3}
                                  disabled={field.key === 'body' && action.config.template_id && loadingTemplate}
                                  className={`${focusedField?.actionIndex === index && focusedField?.fieldKey === field.key ? 'ring-2 ring-blue-400 border-blue-400' : ''}`}
                                />
                              ) : field.type === 'number' ? (
                                <Input
                                  type="number"
                                  value={action.config[field.key] || field.default || ''}
                                  onChange={(e) => updateAction(index, { ...action.config, [field.key]: e.target.value })}
                                />
                              ) : field.type === 'time' ? (
                                <Input
                                  type="time"
                                  value={action.config[field.key] || field.default || '09:00'}
                                  onChange={(e) => updateAction(index, { ...action.config, [field.key]: e.target.value })}
                                  className="w-32"
                                />
                              ) : field.type === 'boolean' ? (
                                <Switch
                                  checked={action.config[field.key] || false}
                                  onCheckedChange={(v) => updateAction(index, { ...action.config, [field.key]: v })}
                                />
                              ) : field.type === 'key_value' ? (
                                <KeyValueEditor
                                  value={action.config[field.key] || {}}
                                  onChange={(newVal) => updateAction(index, { ...action.config, [field.key]: newVal })}
                                />
                              ) : field.type === 'attachments' ? (
                                <div className="space-y-2">
                                  {/* Show template attachments if present */}
                                  {action.config._template_attachments?.length > 0 && (
                                    <div className="bg-green-50 p-2 rounded border border-green-200">
                                      <p className="text-xs text-green-700 font-medium mb-1">📎 Adjuntos de la plantilla:</p>
                                      <ul className="text-xs text-green-600">
                                        {action.config._template_attachments.map((att, i) => (
                                          <li key={i}>• {typeof att === 'string' ? att.split('/').pop() : att.filename || att.name || 'Archivo'}</li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  <Input
                                    ref={(ref) => registerFieldRef(index, field.key, ref)}
                                    value={action.config[field.key] || ''}
                                    onChange={(e) => updateAction(index, { ...action.config, [field.key]: e.target.value })}
                                    onFocus={() => setFocusedField({ actionIndex: index, fieldKey: field.key })}
                                    onDrop={(e) => handleFieldDrop(e, index, field.key)}
                                    onDragOver={handleFieldDragOver}
                                    placeholder="URL de archivo adicional (opcional)"
                                    className={`${focusedField?.actionIndex === index && focusedField?.fieldKey === field.key ? 'ring-2 ring-blue-400 border-blue-400' : ''}`}
                                  />
                                </div>
                              ) : (
                                <Input
                                  ref={(ref) => registerFieldRef(index, field.key, ref)}
                                  value={action.config[field.key] || ''}
                                  onChange={(e) => updateAction(index, { ...action.config, [field.key]: e.target.value })}
                                  onFocus={() => setFocusedField({ actionIndex: index, fieldKey: field.key })}
                                  onDrop={(e) => handleFieldDrop(e, index, field.key)}
                                  onDragOver={handleFieldDragOver}
                                  placeholder={field.placeholder || ''}
                                  disabled={field.key === 'subject' && action.config.template_id && loadingTemplate}
                                  className={`${focusedField?.actionIndex === index && focusedField?.fieldKey === field.key ? 'ring-2 ring-blue-400 border-blue-400' : ''}`}
                                />
                              )}
                            </div>
                          ))}
                        </div>
                      </Card>
                    );
                  })}
                </div>

                {/* Visual Flow Connector */}
                {actions.length > 0 && (
                  <div className="flex justify-center">
                    <div className="w-0.5 h-6 bg-gray-300"></div>
                  </div>
                )}

                {/* Add Action Button */}
                <Button
                  variant="outline"
                  onClick={() => setShowActionPicker(true)}
                  className="w-full border-dashed border-2 hover:border-[#D4A843] hover:bg-[#D4A843]/5"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  Agregar Acción
                </Button>

                {/* Variables Reference - Interactive */}
                <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                    <Sparkles size={16} />
                    Variables Disponibles
                    <span className="text-xs font-normal text-blue-600 ml-2">
                      (Clic para insertar • Arrastra a un campo)
                    </span>
                  </h4>
                  {focusedField && (
                    <div className="mb-3 p-2 bg-green-100 border border-green-300 rounded-lg text-xs text-green-700 flex items-center gap-2">
                      <MousePointer size={14} />
                      Campo activo: <strong>{focusedField.fieldKey}</strong> - Haz clic en una variable para insertarla
                    </div>
                  )}
                  <div className="space-y-2">
                    {config?.variable_categories?.map(cat => (
                      <div key={cat.name}>
                        <p className="text-xs text-gray-500 mb-1">{cat.name}</p>
                        <div className="flex flex-wrap gap-1">
                          {cat.variables.map(v => (
                            <code 
                              key={v} 
                              draggable
                              onDragStart={(e) => handleVariableDragStart(e, v)}
                              className="px-2 py-1 bg-white text-blue-700 rounded text-xs cursor-grab hover:bg-blue-100 hover:shadow-md border border-blue-200 transition-all active:cursor-grabbing flex items-center gap-1 select-none"
                              onClick={() => insertVariableAtCursor(v)}
                              title="Clic para insertar en el cursor • Arrastra a un campo"
                            >
                              <GripHorizontal size={10} className="text-gray-400" />
                              {v}
                            </code>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-blue-600 mt-3 italic">
                    💡 Tip: Haz clic en un campo de texto, luego clic en la variable para insertarla en la posición del cursor
                  </p>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </div>

        <DialogFooter className="border-t pt-4">
          <Button variant="outline" onClick={onClose} data-testid="cancel-automation-btn">Cancelar</Button>
          <Button 
            onClick={handleSave} 
            disabled={saving || !name || !trigger.type || actions.length === 0}
            className="bg-[#D4A843] hover:bg-[#c49a3a]"
            data-testid="save-automation-btn"
          >
            {saving ? (
              <>
                <Loader2 className="animate-spin mr-2 h-4 w-4" />
                Guardando...
              </>
            ) : (
              <>
                <CheckCircle className="mr-2 h-4 w-4" />
                {automation ? 'Guardar Cambios' : 'Crear Automatización'}
              </>
            )}
          </Button>
        </DialogFooter>

        {/* Action Picker Modal */}
        <Dialog open={showActionPicker} onOpenChange={setShowActionPicker}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Zap className="text-[#D4A843]" size={20} />
                Agregar Acción
              </DialogTitle>
              <DialogDescription>Selecciona el tipo de acción a ejecutar en tu automatización</DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6">
              {config?.action_categories?.map(category => {
                const categoryActions = config?.action_types?.filter(a => a.category === category.id);
                if (!categoryActions?.length) return null;
                
                return (
                  <div key={category.id}>
                    <h4 className="font-medium text-gray-700 mb-3 flex items-center gap-2">
                      {category.icon && ICON_MAP[category.icon] && React.createElement(ICON_MAP[category.icon], { size: 16 })}
                      {category.name}
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {categoryActions.map(action => {
                        const IconComponent = ICON_MAP[action.icon] || Play;
                        return (
                          <div
                            key={action.id}
                            onClick={() => !action.coming_soon && addAction(action.id)}
                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                              action.coming_soon 
                                ? 'opacity-50 cursor-not-allowed bg-gray-50' 
                                : 'hover:border-[#D4A843] hover:bg-[#D4A843]/5 hover:shadow-md'
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`p-2 rounded-lg ${
                                action.coming_soon ? 'bg-gray-100' : 'bg-blue-100 text-blue-600'
                              }`}>
                                <IconComponent size={20} />
                              </div>
                              <div className="flex-1">
                                <div className="font-medium text-sm flex items-center gap-2">
                                  <span>{action.name}</span>
                                  {action.coming_soon && (
                                    <Badge variant="secondary" className="text-xs">Próximamente</Badge>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-1">{action.description}</p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};

export default AutomationsPage;
